import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trophy, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const MEDAL = { 1: "bg-amber-100 border-amber-300", 2: "bg-slate-100 border-slate-300", 3: "bg-orange-100 border-orange-300" };
const nf = (n) => Number(n).toLocaleString("it-IT", { maximumFractionDigits: 2 });

export function Leaderboard() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState("all");
  const [rows, setRows] = useState([]);
  const params = month === "all" ? { year } : { year, month: Number(month) };
  useEffect(() => {
    api.get("/reports/leaderboard", { params }).then((r) => setRows(r.data.rows)).catch(() => setRows([]));
  }, [year, month]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async () => {
    try {
      const res = await api.get("/reports/leaderboard/pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `classifica-ore-${year}${month === "all" ? "" : `-${String(month).padStart(2, "0")}`}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Impossibile scaricare il PDF");
    }
  };
  const max = rows[0]?.hours || 1;

  return (
    <section className="rounded-2xl border border-border bg-card p-6 space-y-4 mt-8" data-testid="leaderboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg font-bold flex items-center gap-2"><Trophy className="h-5 w-5 text-amber-500" /> Classifica ore</h2>
        <div className="flex flex-wrap gap-2">
          <Select value={String(month)} onValueChange={setMonth}>
            <SelectTrigger className="w-36" data-testid="leaderboard-month"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutto l'anno</SelectItem>
              {MESI.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-24" data-testid="leaderboard-year"><SelectValue /></SelectTrigger>
            <SelectContent>{Array.from({ length: 5 }, (_, i) => now.getFullYear() - i).map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={download} data-testid="leaderboard-pdf" className="rounded-full h-10"><FileDown className="h-4 w-4 mr-1" /> PDF</Button>
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="leaderboard-empty">Nessuna ora registrata nel periodo.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.user_id} data-testid={`leaderboard-row-${r.rank}`} className={`rounded-lg border px-3 py-2 ${MEDAL[r.rank] || "border-border"}`}>
              <div className="flex items-center gap-3 text-sm">
                <span className={`w-7 text-center font-bold ${r.rank <= 3 ? "text-lg" : "text-muted-foreground"}`}>{r.rank}</span>
                <span className="flex-1 font-medium truncate">{r.name}</span>
                <span className="text-xs text-muted-foreground">{r.services} servizi</span>
                <span className="font-bold tabular-nums w-20 text-right">{nf(r.hours)} h</span>
              </div>
              <div className="ml-10 mt-1 h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full bg-[#D32F2F]" style={{ width: `${(r.hours / max) * 100}%` }} /></div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
